import { useEffect, useState } from 'react'
import { Msg } from '~/common/localizationUtils'
import { Timestamp } from '~/components/Timestamp'
import { AlertBanner, AlertDescription, Box } from '~/design_system'
import { useBannersQuery } from '~/settings/entities/api'

type BannerType = 'info' | 'warning' | 'error'

export type BannerApiResponse = {
  [key: string]: Omit<BannerProps, 'key'>
}

export type BannerProps = {
  key: string
  enabled: boolean
  message: string
  messageKey: string
  messageContext: {
    datetime: string
  }
  messageType: BannerType
  userAgents: string[]
}

export type BannersProps = {
  banners?: BannerProps[]
}

const isUserAgentValid = (userAgents: string[]) => {
  if (!userAgents || !userAgents.length) {
    return true
  }

  return userAgents.some(userAgent => RegExp(userAgent).test(window.navigator.userAgent))
}

const mapBannerResponseToBannersArray = (banners?: BannerApiResponse) => {
  if (!banners) return null
  return Object.entries(banners)
    .map(([key, value]) => {
      return { key, ...value }
    })
    .filter(banner => banner.enabled)
}

export const SystemAlertBanners = () => {
  const { data } = useBannersQuery()
  const banners = mapBannerResponseToBannersArray(data) ?? []
  const [visibleBanners, setVisibleBanners] = useState([] as BannerProps[])

  useEffect(() => {
    /*
      eslint-disable-next-line react-hooks/set-state-in-effect --

      ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: Filtering visible banners from data causes extra render.
      Possible fix approach: Compute filtered banners inline and track dismissals separately:
        // Compute all valid banners inline (memoization can be handled manually if needed)
        const allValidBanners = banners.filter(({ enabled, userAgents }) => enabled && isUserAgentValid(userAgents))
        // Track dismissed banner keys in state
        const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(new Set())
        const visibleBanners = allValidBanners.filter(b => !dismissedKeys.has(b.key))
        const onCloseBanner = (key) => setDismissedKeys(prev => new Set([...prev, key]))
    */
    setVisibleBanners(banners.filter(({ enabled, userAgents }) => enabled && isUserAgentValid(userAgents)))
  }, [data])

  const onCloseBanner = (key: string) => {
    setVisibleBanners(visibleBanners.filter(banner => banner.key !== key))
  }

  if (!banners || !visibleBanners.length) return null

  return (
    <>
      {visibleBanners.map(({ key, messageType, message, messageKey, messageContext }) => {
        const status = messageType ?? 'info'
        return (
          <AlertBanner
            boxShadow="0px 2px 4px rgba(0, 0, 0, 0.12)"
            key={key}
            status={status}
            hasClose
            onClose={() => onCloseBanner(key)}
            variant="system">
            <AlertDescription>
              {messageKey ? (
                <Msg id={messageKey} datetime={<Timestamp datetime={messageContext.datetime} />} />
              ) : (
                <Box sx={{ a: { textDecoration: 'underline' } }} dangerouslySetInnerHTML={{ __html: message }} />
              )}
            </AlertDescription>
          </AlertBanner>
        )
      })}
    </>
  )
}
